<!-- ProductsListView.svelte -->


<script>
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { App } from '@capacitor/app';
  import { Capacitor } from '@capacitor/core';
  import backarrow from './assets/backarrow.png';
  
  const dispatch = createEventDispatcher();
  const imageCloudFrontBaseUrl = 'https://d1fuqkogmwp9nz.cloudfront.net/media/';

  export let categoryId;

  let collections = [];
  let isLoading = false;
  let cartItems = [];
  let totalPrice = 0;
  let totalItems = 0;
  let categoryDetails = null;
  let isAndroidDevice = false;
  let isPopupOpen = false;
  let activeTab = 'home';
  let headerRef;
  let isOrdering = false; 
  let error = null;

  $: totalPrice = cartItems.reduce((sum, item) => sum + (item.quantity * item.inventorywarehousev2__price), 0);
  $: totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  $: getItemQuantity = (product) => {
    const item = cartItems.find(item => item.id === product.id);
    return item ? item.quantity : 0;
  };

  function formatDate(dateString) {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  }

onMount(async () => {
  document.body.classList.add('products-screen-active');
  isAndroidDevice = /android/i.test(navigator.userAgent);
  isLoading = true;

  if (Capacitor.isNativePlatform()) {
    App.addListener('backButton', handleBackButton);
  }

  try {
    const response = await fetchProducts();
    collections = response.collections;
    categoryDetails = response.category_details;
    updateLocalCart(collections);

    // Preload all images before setting isLoading to false
    await preloadImages(collections.flatMap(collection => collection.products));
  } catch (error) {
    console.error('Error fetching products:', error);
  } finally {
    isLoading = false;
  }

  return () => {
    document.body.classList.remove('products-screen-active');
  };
});



  onDestroy(() => {
    document.body.classList.remove('products-screen-active');

    if (Capacitor.isNativePlatform()) {
      App.removeAllListeners();
    }
  });

  async function fetchProducts() {
    error = null; // Reset error before fetching
    const accessToken = localStorage.getItem('accessToken');

    try {
      const response = await fetch('https://groupfruits.online/shop/api/get_products_by_category/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`,
        },
        body: JSON.stringify({ category_id: categoryId }),
      });

      if (!response.ok) {
        throw new Error('Fetch error:' + response.statusText);
      }

      return await response.json();
    } catch (e) {
      error = "Please check your internet connection";
      console.error('Error fetching products:', e);
      throw e;
    }
  }

  async function refreshProducts() {
    isLoading = true;
    try {
      const response = await fetchProducts();
      collections = response.collections;
      categoryDetails = response.category_details;
      updateLocalCart(collections);
      await preloadImages(collections.flatMap(collection => collection.products));
      error = null; // Clear error if fetch is successful
    } catch (e) {
      // Error is already set in fetchProducts
    } finally {
      isLoading = false;
    }
  }


function updateLocalCart(collections) {
  const storedCart = localStorage.getItem(`cart-${categoryId}`);
  let updatedCart = [];

  if (storedCart) {
    const parsedCart = JSON.parse(storedCart);
    const parsedItems = parsedCart.items || parsedCart; // Handle both new and old format
    
    collections.forEach(collection => {
      collection.products.forEach(product => {
        const cartItem = parsedItems.find(item => item.id === product.id);
        if (cartItem) {
          const updatedItem = {
            ...product,
            quantity: Math.min(cartItem.quantity, product.inventorywarehousev2__quantity)
          };
          
          if (updatedItem.quantity > 0) {
            updatedCart.push(updatedItem);
          }
        }
      });
    });
  }

  cartItems = updatedCart;
  saveCart();
}

  function addItemToCart(product) {
    const existingItem = cartItems.find(item => item.id === product.id);
    if (existingItem) {
      updateCartItemQuantity(product.id, existingItem.quantity + 1);
    } else {
      cartItems = [...cartItems, { 
        ...product,
        quantity: 1
      }];
    }
    saveCart();
  }

  function updateCartItemQuantity(productId, newQuantity) {
    if (newQuantity === 0) {
      removeItemFromCart(productId);
    } else {
      cartItems = cartItems.map(item =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      );
      saveCart();
    }
  }

  function removeItemFromCart(productId) {
    cartItems = cartItems.filter(item => item.id !== productId);
    saveCart();
    if (cartItems.length === 0) {
      isPopupOpen = false;
    }
  }

function saveCart() {
  const cartData = {
    items: cartItems,
    lastUpdated: new Date().toISOString()
  };
  localStorage.setItem(`cart-${categoryId}`, JSON.stringify(cartData));
}

  async function checkout() {
    isOrdering = true; 
    try {
      const response = await fetch('https://groupfruits.online/shop/api/CreateOrUpdateCartView/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          category_id: categoryId,
          items: cartItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        localStorage.setItem('checkoutResponse', JSON.stringify(responseData));
        dispatch('checkoutCompleted');
      } else {
        console.error('Error during checkout:', response.statusText);
      }
    } catch (error) {
      console.error('Error during checkout:', error);
    } finally {
      isOrdering = false; // Set to false after the API call completes
    }
  }

  function handleBackButton() {
    if (Capacitor.isNativePlatform()) {
      dispatch('pleaseProceed');
    }
  }

  function togglePopup() {
    isPopupOpen = !isPopupOpen;
  }

  function closePopup() {
    isPopupOpen = false;
  }

  function handleTabClick(tab) {
    activeTab = tab;
    if (tab === 'home') {
      dispatch('pleaseProceed');
    } else if (tab === 'orders') {
      dispatch('pleaseOrderHistory');
    } else if (tab === 'profile') {
      dispatch('profileView');
    }
  }


function preloadImages(products) {
  const imagePromises = products.flatMap(product => 
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = `${imageCloudFrontBaseUrl}${product.image}`;
    })
  );
  return Promise.all(imagePromises);
}



</script>





<style>
  /* Global style */
  :global(body.products-screen-active) {
    padding: 0 !important;
    margin: 0 !important;
    background-color: #fff;
  }

  :global(body) {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
  
  * {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .products-screen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }

  .main-container {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 1.875rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    margin-top: calc(1.875rem + env(safe-area-inset-top));
  }

  .product-container {
    width: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    overflow-x: hidden;
    padding: 2.0625rem calc(0.375rem + env(safe-area-inset-right)) 0 calc(0.5rem + env(safe-area-inset-left));
  }


  .product-card {
    display: flex;
    padding: 0.3125rem 0.3125rem 0.5625rem;
    align-items: center;
    position: relative;
    margin-bottom: 0;
  }

  .product-card + .product-card {
    margin-top: 1.5rem;
  }

  .product-card::after {
    content: "";
    display: block;
    width: calc(100% + 2rem);
    height: 1px;
    background-color: #e0e0e0;
    position: absolute;
    bottom: -0.75rem;
    left: -1rem;
  }

  .product-card:last-of-type::after {
    display: none;
  }

  .product-card.out-of-stock {
    opacity: 0.5;
  }

  .low-stock-label {
    position: absolute;
    top: 0.625rem;
    left: 0.625rem;
    background-color: #FFA500;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }

  .product-details {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding-right: 0.625rem;
  }

  .product-title {
    font-size: 0.875rem;
    margin: 0 0 0.5625rem;
    font-weight: 500;
    line-height: 1.2;
  }

  .product-info {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0.5625rem;
  }

  .product-info span {
    padding: 0.25rem 0.3125rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: #666;
    background-color: #ffffff;
    border: 1px solid #ddd;
    margin-right: 0.1875rem;
  }

  .product-price {
    font-size: 1.125rem;
    font-weight: bold;
    color: #000;
    margin-bottom: 0.5rem;
  }

  .product-image-container {
    width: 13.75rem;
    height: 9.375rem;
    flex-shrink: 0;
    position: relative;
  }

  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.5rem;
  }

  .add-button, .quantity-button {
    position: absolute;
    bottom: 0;
    right: 3rem;
    width: 7.5rem;
    height: 2.5rem;
    border-radius: 0.3125rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .add-button {
    background-color: white;
    color: #FF0F39;
    border: 1px solid #FF0F39;
  }

  .quantity-button {
    background-color: #FF0F39;
    color: white;
    border: none;
    justify-content: space-between;
    margin-bottom: 0.4375rem;
  }

  .quantity-button button {
    background-color: transparent;
    color: white;
    border: none;
    font-size: 1.125rem;
    cursor: pointer;
    padding: 0;
    margin: 0;
    width: 1.875rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .quantity-button span {
    font-size: 0.875rem;
  }

  .cart-summary {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff4f6;
    padding: 0.25rem 0.1875rem 0.0625rem 1.1875rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: white;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  }

  .cart-summary-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    flex: 0 0 25%;
  }

  .cart-summary-left span {
    font-size: 0.875rem;
    color: #231F1F;
  }

  .order-button {
    background-color: #FF0F39;
    color: #FFFFFF;
    border: none;
    padding: 1.125rem 1.25rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    cursor: pointer;
    margin: 0;
    transition: background-color 0.3s ease;
    flex: 1 0 auto;
  }

  .order-button:hover {
    background-color: #c6283d;
  }

  .order-button.loading {
    position: relative;
    opacity: 0.7;
    cursor: wait;
  }

  .order-button.loading::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    border: 0.1875rem solid #fff;
    border-top-color: transparent;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }

  .final-bottom-padding {
    height: 3.75rem;
  }

  .popup-overlay {
    position: fixed;
    top: env(safe-area-inset-top);
    left: env(safe-area-inset-left);
    right: env(safe-area-inset-right);
    bottom: calc(3.125rem + env(safe-area-inset-bottom));
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    z-index: 999;
  }

  .popup-content {
    background-color: white;
    border-radius: 1rem 1rem 0 0;
    max-height: calc(100vh - 10rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    overflow-y: auto;
    position: relative;
    padding-top: 2.5rem;
  }

  .close-button {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3.125rem;
    background-color: #f0f0f0;
    border: none;
    border-radius: 1rem 1rem 0 0;
    font-size: 2.125rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  .popup-inner-content {
    padding: 1.5rem;
  }

  .header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: white;
    padding-top: calc(1.6875rem + env(safe-area-inset-top));
    padding-bottom: 1.6875rem;
    padding-left: calc(1rem + env(safe-area-inset-left));
    padding-right: calc(1rem + env(safe-area-inset-right));
    z-index: 10;
    box-sizing: border-box;
    border-bottom: 0.00625rem solid #e5dada;
    box-shadow: none;
  }




  .back-button {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.5rem;
    height: 1.5rem;
  }

  .back-button img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: pointer;
  }

  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .popup-title {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .popup-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f0f0f0;
    margin-top: 1rem;
  }

  .popup-item-details {
    flex-grow: 1;
    width: 70%;
    margin-right: 0.9375rem;
  }

  .popup-item-title {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    margin-top: 0;
  }

  .popup-item-price {
    font-size: 0.9375rem;
    color: #7B7676;
    margin: 0;
    padding-bottom: 0.9375rem;
  }

  .popup-quantity-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 5rem;
    height: 1.75rem;
    border: 1px solid #FF0F39;
    border-radius: 0.25rem;
    overflow: hidden;
  }

  .popup-quantity-button button {
    background-color: transparent;
    color: #FF0F39;
    border: none;
    font-size: 1.125rem;
    cursor: pointer;
    padding: 0;
    width: 1.75rem;
    height: 1.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
  }

  .popup-quantity-button span {
    font-size: 0.875rem;
    color: #FF0F39;
    font-weight: bold;
  }

  .popup-item-subtotal {
    font-size: 0.9375rem;
    color: #231F1F;
    margin-top: 0.5rem;
    text-align: center;
    font-weight: 600;
    margin-bottom: 0;
  }


.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #FF0F39;
  border-radius: 50%;
  animation: spin 1s linear infinite, pulse 2s ease-in-out infinite;
}

.loading-text {
  margin-top: 20px;
  font-size: 18px;
  color: #333;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}



  .bottom-indicator {
    background: linear-gradient(90deg, #f5eeef, #ddd2d0);
    color: #535445;
    padding: 0.3125rem;
    text-align: center;
    font-size: 0.8rem;
    font-weight: bold;
    border-radius: 0.375rem;
    margin-top: 1.875rem;
    margin-bottom: 1.875rem;
    position: relative;
    overflow: hidden;
  }

  .bottom-indicator::before,
  .bottom-indicator::after {
    content: '';
    position: absolute;
    width: 1.25rem;
    height: 0.125rem;
    background: rgba(255, 255, 255, 0.7);
    animation: slide 3s infinite ease-in-out;
  }

  .bottom-indicator::before {
    top: 40%;
    left: -1.25rem;
    animation-delay: 0.5s;
  }

  .bottom-indicator::after {
    top: 60%;
    right: -1.25rem;
    animation-delay: 1.5s;
  }

  @keyframes slide {
    0% { transform: translateX(0); }
    50% { transform: translateX(3.125rem); }
    100% { transform: translateX(0); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 1.25rem;
  }

  .refresh-button {
    margin-top: 1.25rem;
    padding: 0.625rem 1.25rem;
    background-color: #FF0F39;
    color: white;
    border: none;
    border-radius: 0.3125rem;
    font-size: 1rem;
    cursor: pointer;
  }

  .refresh-button:hover {
    background-color: #c6283d;
  }
</style>

<div class="products-screen">
    <div class="header" bind:this={headerRef}>
		<span class="back-button" on:click={() => dispatch('pleaseProceed')}>
		  <img src={backarrow} alt="Back" />
		</span>
    </div>


  <div class="main-container">

    <div class="product-container">

		{#if isLoading}
		  <div class="loading-screen">
			<div class="loader">
			  <div class="spinner"></div>
			  <div class="loading-text">Loading products...</div>
			</div>
		  </div>
		{:else if error}
		
		  <div class="error-container">
			<p>{error}</p>
			<button on:click={refreshProducts} class="refresh-button">Refresh</button>
		  </div>
		{:else}


        {#each collections as collection}
          {#each collection.products as product}
		  
			  <div class="product-card" class:out-of-stock={product.label === 'Out of stock'} class:low-stock={product.label === 'Low stock'}>
				<div class="product-details">
				  <h3 class="product-title">{product.title}</h3>
				  <div class="product-info">
					<span>{product.unit} {product.measurement_unit}</span>
					{#if product.approximate_unit}
					  <span>{product.approximate_unit}</span>
					{/if}
				  </div>
				  <span class="product-price">₹{product.inventorywarehousev2__price.toFixed(0)}</span>
				</div>
				<div class="product-image-container">
				  <img 
					class="product-image" 
					src="{imageCloudFrontBaseUrl}{product.image}"
					alt="{product.title}"
				  />

					{#if product.label === 'Out of stock'}
					  <button class="add-button" disabled>Out of stock</button>
					{:else}
					  {#if product.label === 'Low stock'}
						<div class="low-stock-label">Only {product.inventorywarehousev2__quantity} available</div>
					  {/if}
					  {#if getItemQuantity(product) === 0}
						<button class="add-button" on:click={() => addItemToCart(product)}>+ ADD</button>
					  {:else}
						<div class="quantity-button">
						  <button on:click={() => updateCartItemQuantity(product.id, getItemQuantity(product) - 1)}>-</button>
						  <span>{getItemQuantity(product)}</span>
						  <button on:click={() => addItemToCart(product)}>+</button>
						</div>
					  {/if}
					{/if}
				  
				  
				</div>
			  </div>
			
			
			
          {/each}
        {/each}
		<div class="bottom-padding"></div>



      <div class="bottom-indicator">End</div>



		{#if totalItems > 0}
		  <div class="final-bottom-padding"></div>
		{/if}
      {/if}
	  
    </div>
  </div>



    {#if totalItems > 0}
      <div class="cart-summary">
        <div class="cart-summary-left" on:click={togglePopup}>

          <span>{totalItems} ITEMS</span>
          <span>{isPopupOpen ? '▼' : '▲'}</span>
        </div>

		<button class="order-button" on:click={checkout} class:loading={isOrdering}>
		  {isOrdering ? 'Ordering...' : 'Order ₹' + Math.round(totalPrice)}
		</button>

      </div>
    {/if}

    {#if isPopupOpen}
      <div class="popup-overlay" on:click={closePopup}>
        <div class="popup-content" on:click|stopPropagation>
          <button class="close-button" on:click={closePopup}>
            <span>×</span>
          </button>
          <div class="popup-inner-content">
            <div class="popup-header">
              <h2 class="popup-title">Total Items ({totalItems})</h2>
            </div>
            {#each cartItems as item}
              <div class="popup-item">
                <div class="popup-item-details">
                  <h3 class="popup-item-title">{item.title}</h3>
                  <p class="popup-item-price">{item.unit} {item.measurement_unit}   •   ₹{item.inventorywarehousev2__price.toFixed(0)}</p>
                </div>
                <div>
                  <div class="popup-quantity-button">
                    <button on:click={() => updateCartItemQuantity(item.id, item.quantity - 1)}>−</button>
                    <span>{item.quantity}</span>
                    <button on:click={() => addItemToCart(item)}>+</button>
                  </div>
                  <p class="popup-item-subtotal">₹{(item.quantity * item.inventorywarehousev2__price).toFixed(0)}</p>
                </div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/if}
	
</div>

